/**
 * AI Scribe — raw PCM capture constants
 *
 * The streaming ASR socket (`/ws/stream`) decodes **raw 16-bit little-endian
 * PCM, mono, 16 kHz** — not a container format. Every value the worklet and the
 * capture hook depend on lives here.
 *
 * @author [Rajkumar Rathod]
 */

/** Sample rate the socket expects. The worklet downsamples the device rate to this. */
export const PCM_TARGET_SAMPLE_RATE = 16_000;

/**
 * Samples per posted frame. 2048 @ 16 kHz ≈ 128 ms of audio (4 KB per send),
 * small enough for low-latency partials, large enough to avoid socket churn.
 */
export const PCM_FRAME_SIZE = 2048;

/** Name the worklet registers itself under. */
export const PCM_PROCESSOR_NAME = "pcm-processor";

/** Mic constraints — ASR quality drops without the browser DSP chain. */
export const PCM_AUDIO_CONSTRAINTS: MediaTrackConstraints = {
  channelCount: 1,
  echoCancellation: true,
  noiseSuppression: true,
  autoGainControl: true,
};

/**
 * The worklet source, inlined so no static asset has to be served.
 *
 * It decimates the incoming Float32 render quantum to `PCM_TARGET_SAMPLE_RATE`,
 * converts to Int16, and posts fixed-size frames back with a zero-copy transfer.
 * The fractional read cursor carries across quanta so no sample drift builds up
 * over a long visit.
 */
export const PCM_WORKLET_SOURCE = `
class PcmProcessor extends AudioWorkletProcessor {
  constructor(options) {
    super();
    const { targetSampleRate, frameSize } = options.processorOptions;
    this.ratio = sampleRate / targetSampleRate;
    this.frameSize = frameSize;
    this.cursor = 0;
    this.pending = [];
  }

  process(inputs) {
    const channel = inputs[0] && inputs[0][0];
    if (!channel || channel.length === 0) return true;

    while (this.cursor < channel.length) {
      const sample = Math.max(-1, Math.min(1, channel[Math.floor(this.cursor)]));
      this.pending.push(sample < 0 ? sample * 0x8000 : sample * 0x7fff);
      this.cursor += this.ratio;
    }
    this.cursor -= channel.length;

    while (this.pending.length >= this.frameSize) {
      const frame = Int16Array.from(this.pending.splice(0, this.frameSize));
      this.port.postMessage(frame.buffer, [frame.buffer]);
    }

    return true;
  }
}

registerProcessor(${JSON.stringify(PCM_PROCESSOR_NAME)}, PcmProcessor);
`;

/** `addModule` takes a URL — the worklet source is handed over as a data URL. */
export const PCM_WORKLET_MODULE_URL = `data:application/javascript,${encodeURIComponent(
  PCM_WORKLET_SOURCE
)}`;

/** `AudioContext.state` values the capture hook branches on. */
export const AUDIO_CONTEXT_STATE = {
  RUNNING: "running",
  SUSPENDED: "suspended",
  CLOSED: "closed",
} as const;
